import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, TextArea } from '@insight-health-ai/roseate/components';
import { Keyboard02, Attachment02 } from '@insight-health-ai/roseate/icons';
import {
  SendOutlined,
  AudioOutlined,
  LoadingOutlined,
  DeleteFilled,
} from '@ant-design/icons';
import {
  useBeforeUnload,
  useScreenSize,
} from '@insight-health-ai/roseate/utils';
import { useChatStore } from 'src/hooks/chat';
import { useSpeechToText } from 'src/hooks/common';
import { useAuthStore } from 'src/hooks/auth';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { analytics } from 'src/services/analytics';
import { getAppointmentDetailsForAnalytics } from 'src/utils';
import { PauseIcon } from 'src/icons/Pause.icon';
import { BigMicIcon } from 'src/icons/BigMic';
import { BigPauseIcon } from 'src/icons/BigPause';
import { useTranslation } from 'react-i18next';
import { i18n } from 'src/services/i18n';

type ChatControlsProps = {
  onSend: (message: string) => void;
};

export type ChatControlsRef = {
  forceSetMessage: (message: string) => void;
};

let emptySendClicks = 0;

const ChatControls = forwardRef<ChatControlsRef, ChatControlsProps>(
  ({ onSend }, ref) => {
    const { mediaMax, mediaMin } = useScreenSize();
    const { appointment } = useAuthStore();
    const {
      speechToText,
      setSpeechToText,
      setTextToSpeech,
      smartReplies,
      loading,
      error,
      chat,
    } = useChatStore();
    const [message, setMessage] = useState('');
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const sttMessageContainerRef = useRef<HTMLDivElement | null>(null);
    const inputResetRef = useRef(0);
    const [controlType, setControlType] = useState<'keyboard' | 'mic'>(
      mediaMax('sm') ? 'mic' : 'keyboard'
    );
    const { t } = useTranslation('vca');

    const eventData = useMemo(() => {
      return getAppointmentDetailsForAnalytics(appointment!);
    }, [appointment]);

    const forceSetMessage: typeof setMessage = (message) => {
      inputResetRef.current += 1;
      setMessage(message);
    };

    useImperativeHandle(ref, () => ({
      forceSetMessage,
    }));

    const disabled = !!(
      speechToText === 'loading' ||
      loading ||
      error ||
      chat.loading ||
      chat.error
    );

    useLayoutEffect(() => {
      try {
        if (sttMessageContainerRef.current) {
          sttMessageContainerRef.current.scrollTop =
            sttMessageContainerRef.current.scrollHeight;
        }
        const element = inputRef.current;
        if (element) {
          if (element instanceof HTMLTextAreaElement) {
            element.setSelectionRange(
              element.value.length,
              element.value.length
            );
            element.scrollTop = element.scrollHeight;
          }
        }
      } catch {
        // not a critical operation, ignore if this fails for some reason
      }
    }, [inputResetRef.current]);

    useEffect(() => {
      if (!disabled) {
        inputRef.current?.focus?.();
      }
    }, [disabled]);

    /**
     * Prompt user to save the chat before leaving the page
     */
    useBeforeUnload({ enabled: !!message });

    const enabled = speechToText !== 'off';
    const { resetStream } = useSpeechToText({
      enabled,
      language: appointment?.patient_preferred_language || i18n.language,
      onMessage: (data, type) => {
        if (type === 'final') {
          forceSetMessage((prev) => `${prev} ${data}`);
        }
      },
      onError: (error: any) => {
        let errorMessage = t('speechToText.error');
        if (
          error?.name === 'NotAllowedError' ||
          (error?.message || '').toLowerCase().includes('permission denied') ||
          (error?.message || '').toLowerCase().includes('denied permission')
        ) {
          errorMessage = t('speechToText.permissionDeniedError');
        }
        toast.error(errorMessage);
        setSpeechToText('off');
        setTextToSpeech('off');
      },
      onConnect: () => {
        if (enabled) {
          setSpeechToText('on');
        }
      },
    });

    const handleSend = (message: string) => {
      const trimmedMessage = (message || '').trim();
      if (!trimmedMessage) {
        emptySendClicks += 1;
        if (emptySendClicks >= 3) {
          toast.info(t('emptyMessageWarning'));
          emptySendClicks = 0;
        }
        return;
      }
      onSend(trimmedMessage);
      forceSetMessage('');
      resetStream();
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setControlType('keyboard');
      setMessage(e.target.value);
    };

    const startRecording = () => {
      analytics.track('Started Microphone Lumi', eventData);
      setTextToSpeech('on');
      setSpeechToText('loading');
    };

    const stopRecording = () => {
      analytics.track('Stopped Microphone Lumi', eventData);
      setSpeechToText('off');
      setTextToSpeech('off');
    };

    const renderMicButton = () => {
      const commonProps = {
        variant: 'outlined',
        size: 'lg',
      } as const;

      if (speechToText === 'on') {
        return <PauseIcon fill="#d9534f" onClick={stopRecording} />;
      }

      if (speechToText === 'loading') {
        return (
          <Button
            {...commonProps}
            appearance="danger"
            icon={<LoadingOutlined />}
            onClick={stopRecording}
          />
        );
      }

      return (
        <Button
          {...commonProps}
          appearance="primary"
          icon={<AudioOutlined />}
          onClick={() => {
            if (mediaMax('sm')) {
              setControlType('mic');
            }
            startRecording();
          }}
        />
      );
    };

    const smartRepliesJsx =
      smartReplies.length > 0 ? (
        <div className="flex justify-start gap-2 flex-0 py-2 items-center">
          {smartReplies.map((reply) => (
            <Button
              key={reply}
              size="sm"
              variant="outlined"
              onClick={() => {
                analytics.track('Smart Replies Clicked', {
                  ...getAppointmentDetailsForAnalytics(appointment!),
                  smart_reply_response_value: reply,
                });
                handleSend(reply);
              }}
            >
              {reply}
            </Button>
          ))}
        </div>
      ) : null;

    if (controlType === 'mic') {
      const renderMobileMic = () => {
        if (speechToText === 'on') {
          return (
            <div className="flex gap-3 items-center">
              <div
                id="vca-mic-icon-stop"
                className="flex-0 shadow-xl rounded-full cursor-pointer"
              >
                <BigPauseIcon size="sm" onClick={stopRecording} />
              </div>
              <div className="flex text-gray-300 items-center">
                {t('speechToText.onLabel')}
              </div>
            </div>
          );
        }

        if (speechToText === 'loading') {
          return (
            <div className="flex gap-3 items-center">
              <div
                id="vca-mic-icon-loading"
                className="flex-0 shadow-xl rounded-full cursor-pointer"
              >
                <BigPauseIcon indeterminate size="sm" onClick={stopRecording} />
              </div>
              <div className="flex text-gray-300 items-center">
                {t('speechToText.loadingLabel')}
              </div>
            </div>
          );
        }

        return (
          <div className="flex gap-3 items-center" onClick={startRecording}>
            <div
              id="vca-mic-icon-start"
              className="flex-0 shadow-xl rounded-full cursor-pointer"
            >
              <BigMicIcon size="sm" />
            </div>
            <div className="flex text-gray-300 items-center">
              {t('speechToText.offLabel')}
            </div>
          </div>
        );
      };

      return (
        <div id="vca-chat-controls" data-mobile className="flex-0">
          <div className="px-3">{smartRepliesJsx}</div>
          <div className="flex flex-col gap-2 flex-0 p-3 bg-white border-t border-gray-100 shadow-lg">
            {message?.length > 0 && (
              <div
                ref={sttMessageContainerRef as any}
                style={{ maxHeight: 64, overflow: 'auto' }}
              >
                {message}
                <div id="scroll-manager" className="hidden" />
              </div>
            )}
            <div className="flex flex-0 gap-3 items-center">
              {renderMobileMic()}
              <div className="flex gap-2 flex-0 right-0 ml-auto">
                {message?.length > 0 && (
                  <Button
                    icon={<DeleteFilled />}
                    size="lg"
                    appearance="danger"
                    variant="outlined"
                    onClick={() => {
                      analytics.track('Clicked Delete In Clear Voice Text', {});
                      forceSetMessage('');
                    }}
                  />
                )}
                <Button
                  // @ts-ignore
                  id="vca-send-button"
                  icon={<SendOutlined />}
                  size="lg"
                  appearance="primary"
                  onClick={() => handleSend(message)}
                />
                {/* <Button
                  // @ts-ignore
                  id="vca-attachment-button"
                  icon={<Attachment02 className="text-2xl" />}
                  size="lg"
                  appearance="primary"
                  variant="outlined"
                  onClick={() => { }}
                /> */}
                {speechToText == 'off' && (
                  <Button
                    icon={<Keyboard02 className="text-3xl" />}
                    size="lg"
                    appearance="primary"
                    variant="outlined"
                    onClick={() => {
                      setControlType('keyboard');
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div id="vca-chat-controls" className="flex-0 pb-3 px-2 sm:px-4 md:px-8">
        {smartRepliesJsx}
        <div
          style={{
            boxShadow: '0 0 0 0',
            ...(disabled && {
              opacity: 0.5,
              pointerEvents: 'none',
              cursor: 'not-allowed',
            }),
          }}
          className="relative flex w-full p-2 flex-0 justify-center items-center border rounded-lg border-gray-200 mt-auto bg-white"
        >
          <TextArea
            id="vca-chat-input"
            autoFocus
            disabled={disabled}
            ref={(ref) => {
              inputRef.current = ref?.resizableTextArea?.textArea || null;
            }}
            autoSize={{
              minRows: 1,
              maxRows: 3,
            }}
            key={inputResetRef.current}
            size={mediaMax('sm') ? 'middle' : 'large'}
            variant="borderless"
            defaultValue={message}
            placeholder={t('controls.placeholder')}
            onChange={handleTextChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!message?.length) {
                  return;
                }
                handleSend(message);
              }
            }}
          />
          <div className="flex gap-2 flex-0">
            <Button
              // @ts-ignore
              id="vca-send-button"
              icon={<SendOutlined />}
              size="lg"
              appearance="primary"
              onClick={() => handleSend(message)}
            />
            <div id="vca-mic-icon-container">{renderMicButton()}</div>
          </div>
        </div>
      </div>
    );
  }
);

export { ChatControls };
